//src\layout\MainLayout\Drawer\DrawerContent\Navigation\NavCollapse.tsx

import { useEffect, useState, Dispatch, MouseEvent, SetStateAction } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// material-ui
import { styled, useTheme } from '@mui/material/styles';
import {
  Collapse,
  ClickAwayListener,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popper,
  Typography,
  Fade,
  useMediaQuery
} from '@mui/material';

// project-imports
import NavItem from './NavItem';
import Dot from 'components/@extended/Dot';
import SimpleBar from 'components/third-party/SimpleBar';

import { dispatch, useSelector } from 'store';
import { activeItem } from 'store/reducers/menu';

// assets
import { ArrowDown2, ArrowUp2, ArrowRight2, Copy } from 'iconsax-react';

// types
import { ThemeMode } from 'types/config';
import { NavItemType } from 'types/menu';
import { MINI_DRAWER_WIDTH } from 'config';

type VirtualElement = {
  getBoundingClientRect: () => ClientRect | DOMRect;
  contextElement?: Element;
};

// mini-menu - wrapper
const PopperStyled = styled(Popper)(({ theme }) => ({
  overflow: 'visible',
  zIndex: 1202,
  minWidth: 200,
  height: '100vh',
  '&:before': {
    content: '""',
    display: 'block',
    position: 'absolute',
    top: '50%',
    left: 0,
    width: 10,
    height: 10,
    backgroundColor: theme.palette.background.paper,
    transform: 'translateY(-50%) rotate(45deg)',
    zIndex: 120,
    borderLeft: `1px solid ${theme.palette.divider}`,
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  transform: `translateX(${MINI_DRAWER_WIDTH}px) !important`
}));

// ==============================|| NAVIGATION - COLLAPSE ||============================== //

interface Props {
  menu: NavItemType;
  level: number;
  parentId: string;
  setSelectedItems: Dispatch<SetStateAction<string | undefined>>;
  selectedItems: string | undefined;
  setSelectedLevel: Dispatch<SetStateAction<number>>;
  selectedLevel: number;
  lastItem?: number;
  remItems?: NavItemType[];
  lastItemId?: string;
}

const NavCollapse = ({
  menu,
  level,
  parentId,
  setSelectedItems,
  selectedItems,
  setSelectedLevel,
  selectedLevel,
  lastItem,
  remItems,
  lastItemId
}: Props) => {
  const theme = useTheme();
  const navigation = useNavigate();
  const downLG = useMediaQuery(theme.breakpoints.down('lg'));

  const { drawerOpen } = useSelector((state) => state.menu);

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<string | null | undefined>(null);
  const [anchorEl, setAnchorEl] = useState<VirtualElement | (() => VirtualElement) | null | undefined>(null);
  const [delayClose, setDelayClose] = useState<NodeJS.Timeout | null>(null);

  const handleClick = (event: MouseEvent<HTMLElement> | undefined) => {
    setAnchorEl(null);
    setSelectedLevel(level);
    if (drawerOpen) {
      setOpen(!open);
      setSelected(!selected ? menu.id : null);
      setSelectedItems(!selected ? menu.id : '');
      if (menu.url) navigation(`${menu.url}`);
    } else {
      if (delayClose) clearTimeout(delayClose);
      setAnchorEl(event?.currentTarget);
    }
  };

  const handlerIconLink = () => {
    if (!drawerOpen) {
      if (menu.url) navigation(`${menu.url}`);
      // setSelected(menu.id);// commented for the multiple modules highlighted issue.
    }
  };

  const miniMenuOpened = Boolean(anchorEl);

  const handleClose = () => {
    if (delayClose) clearTimeout(delayClose);
    const timeout = setTimeout(() => {
      setAnchorEl(null);
    }, 500);
    setDelayClose(timeout);
  };

  useEffect(() => {
    if (selected === selectedItems) {
      if (level === 1) {
        setOpen(true);
      }
    } else {
      if (level === selectedLevel) {
        setOpen(false);
        if ((!miniMenuOpened && !drawerOpen && !selected) || drawerOpen) {
          setSelected(null);
        }
      }
    }
  }, [selectedItems, level, selected, miniMenuOpened, drawerOpen, selectedLevel]);

  const { pathname } = useLocation();

  useEffect(() => {
    if (pathname === menu.url) {
      setSelected(menu.id);
    }
  }, [pathname, menu.id, menu.url]);

  const checkOpenForParent = (child: NavItemType[], id: string) => {
    child.forEach((item: NavItemType) => {
      if (item.url === pathname) {
        setOpen(true);
        setSelected(id);
      }
    });
  };

  useEffect(() => {
    setOpen(false);
    if (!miniMenuOpened) {
      setSelected(null);
    }
    if (miniMenuOpened) setAnchorEl(null);
    if (menu.children) {
      menu.children.forEach((item: NavItemType) => {
        if (item.children?.length) {
          checkOpenForParent(item.children, menu.id!);
        }
        if (pathname && pathname.includes('product-details')) {
          if (item.url && item.url.includes('product-details')) {
            setSelected(menu.id);
            setOpen(true);
          }
        }
        if (item.url === pathname) {
          setSelected(menu.id);
          setOpen(true);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, menu.children]);

  useEffect(() => {
    if (menu.url === pathname) {
      dispatch(activeItem({ openItem: [menu.id] }));
      setSelected(menu.id);
      setAnchorEl(null);
      setOpen(true);
    }
  }, [pathname, menu]);

  const navCollapse = menu.children?.map((item) => {
    switch (item.type) {
      case 'collapse':
        return (
          <NavCollapse
            key={item.id}
            setSelectedItems={setSelectedItems}
            setSelectedLevel={setSelectedLevel}
            selectedLevel={selectedLevel}
            selectedItems={selectedItems}
            menu={item}
            level={level + 1}
            parentId={parentId}
          />
        );
      case 'item':
        return <NavItem key={item.id} item={item} level={level + 1} />;
      default:
        return (
          <Typography key={item.id} variant="h6" color="error" align="center">
            Fix - Collapse or Item
          </Typography>
        );
    }
  });

  const isSelected = selected === menu.id;
  const borderIcon = level === 1 ? <Copy variant="Bulk" size={drawerOpen ? 22 : 24} /> : false;
  const Icon = menu.icon!;
  const menuIcon = menu.icon ? <Icon variant="Bulk" size={drawerOpen ? 22 : 24} /> : borderIcon;
  const textColor = theme.palette.mode === ThemeMode.DARK ? theme.palette.common.white : theme.palette.common.black;
  const iconSelectedColor = theme.palette.mode === ThemeMode.DARK && drawerOpen ? theme.palette.text.primary : theme.palette.primary.main;

  return (
    <>
      <>
        <ListItemButton
          selected={isSelected}
          className="navigation-menu-items2"
          {...(!drawerOpen && { onMouseEnter: handleClick, onMouseLeave: handleClose })}
          onClick={handleClick}
          sx={{
            pl: drawerOpen ? `${level === 1 ? 20 : level * 20 - 10}px` : 1.5,
            py: !drawerOpen && level === 1 ? 1.25 : 1,
            display: 'flex',
            flexDirection: drawerOpen ? 'row' : 'column',
            ...(drawerOpen && {
              mx: drawerOpen ? `${level === 1 ? 20 : level * 20 - 10}px` : 1.5,
              my: 0.5,
              '&:hover': {
                bgcolor: theme.palette.mode === ThemeMode.DARK ? 'divider' : 'secondary.200'
              },
              '&.Mui-selected': {
                color: iconSelectedColor
              }
            }),
            ...(!drawerOpen && {
              px: 2.75,
              justifyContent: 'center',
              '&:hover': {
                bgcolor: 'transparent'
              },
              '&.Mui-selected': {
                '&:hover': {
                  bgcolor: 'transparent'
                },
                bgcolor: 'transparent'
              }
            })
          }}
        >
          {menuIcon && (
            <ListItemIcon
              onClick={handlerIconLink}
              sx={{
                minWidth: 38,
                color: isSelected ? 'primary.main' : textColor,
                ...(!drawerOpen && {
                  borderRadius: 1,
                  width: 24,
                  height: 24,
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:hover': {
                    bgcolor: theme.palette.mode === ThemeMode.DARK ? 'secondary.light' : 'secondary.200'
                  }
                }),
                ...(!drawerOpen &&
                  isSelected && {
                    bgcolor: theme.palette.mode === ThemeMode.DARK ? 'secondary.100' : 'primary.lighter',
                    '&:hover': {
                      bgcolor: theme.palette.mode === ThemeMode.DARK ? 'secondary.200' : 'primary.lighter'
                    }
                  })
              }}
              className={`${theme.palette.mode === 'dark' ? 'dark' : ''}`}
            >
              {menuIcon}
            </ListItemIcon>
          )}

          {!menuIcon && drawerOpen && (
            <ListItemIcon
              sx={{
                minWidth: 30
              }}
            >
              <Dot size={isSelected ? 6 : 5} color={'secondary'} />
            </ListItemIcon>
          )}
          {!drawerOpen && (
            <Typography
              variant="h6"
              color={textColor}
              sx={{ fontWeight: isSelected ? 500 : 400 }}
              className={`${theme.palette.mode === 'dark' ? 'dark' : ''}`}
            >
              {menu.title}
            </Typography>
          )}

          {(drawerOpen || (!drawerOpen && level !== 1)) && (
            <ListItemText
              primary={
                <Typography
                  variant="h6"
                  color={textColor}
                  sx={{ fontWeight: isSelected ? 500 : 400 }}
                  className={`${theme.palette.mode === 'dark' ? 'dark' : ''}`}
                >
                  {menu.title}
                </Typography>
              }
              secondary={
                menu.caption && (
                  <Typography variant="caption" color="secondary">
                    {menu.caption}
                  </Typography>
                )
              }
            />
          )}
          {(drawerOpen || (!drawerOpen && level !== 1)) &&
            (miniMenuOpened || open ? (
              <>
                {miniMenuOpened ? (
                  <ArrowRight2 size={12} color={textColor} style={{ marginLeft: 1 }} />
                ) : (
                  <ArrowUp2 size={12} color={textColor} style={{ marginLeft: 1 }} />
                )}
              </>
            ) : (
              <ArrowDown2 size={12} color={textColor} style={{ marginLeft: 1 }} />
            ))}

          {!drawerOpen && !downLG && (
            <PopperStyled
              open={miniMenuOpened}
              placement="right-start"
              className="navigation-menu-popover"
              style={{
                zIndex: 2001,
                width: '280px',
                left: '0px'
              }}
              popperOptions={{
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [-12, 1]
                    }
                  }
                ]
              }}
            >
              {({ TransitionProps }) => (
                <Fade in={miniMenuOpened} {...TransitionProps} timeout={500}>
                  <Paper
                    className="navigation-submenu"
                    sx={{
                      overflow: 'hidden',
                      boxShadow: theme.customShadows.z1,
                      backgroundImage: 'none',
                      border: `1px solid ${theme.palette.divider}`,
                      height: '100vh',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <ClickAwayListener onClickAway={handleClose}>
                      <>
                        <SimpleBar
                          sx={{
                            overflowX: 'hidden',
                            overflowY: 'auto',
                            height: '100%'
                          }}
                        >
                          <Typography
                            className="submenu-title"
                            variant="h6"
                            sx={{
                              backgroundColor:
                                theme.palette.mode === 'dark'
                                  ? theme.palette.secondary[200]
                                  : theme.palette.secondary.colorNeutrals?.neutrals20,
                              color: textColor
                            }}
                          >
                            {menu.title}
                          </Typography>
                          {navCollapse}
                        </SimpleBar>
                      </>
                    </ClickAwayListener>
                  </Paper>
                </Fade>
              )}
            </PopperStyled>
          )}
        </ListItemButton>
        {drawerOpen && (
          <Collapse in={open} timeout="auto" unmountOnExit>
            <List sx={{ p: 0 }}>{navCollapse}</List>
          </Collapse>
        )}
      </>
    </>
  );
};

export default NavCollapse;
